import { AdminPublicMenuLayoutPanel } from '../components/AdminPublicMenuLayoutPanel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminAiChatFailureItem, AdminBusinessDetail, AdminBusinessListItem } from '../../shared/types';
import { Badge, Button, Card, EmptyState, Field, HeroSelectField, Input, LoadingScreen, Modal, PageHeader, Table } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useAdminAiChatFailures, useAdminBusinessDetail, useAdminBusinesses, useAdminOverview } from '../hooks/businessData';
import { apiRequest, jsonBody } from '../lib/api';
import { queryKeys } from '../../lib/queryKeys';

interface Overview { businesses: number; activeBranches: number; activeOrders: number; activeBusinessUsers: number }
type BusinessListItem = AdminBusinessListItem;
type BusinessDetail = AdminBusinessDetail;

const emptyBusiness = {
  name: '', subdomain: '', ownerName: '', ownerEmail: '', ownerPassword: '',
  branchName: '', address: '', lat: '10.4806', lng: '-66.9036', radius: '150', whatsapp: '', receptionPin: '', kitchenPin: '', deliveryPin: '',
};
const emptyBranch = { name: '', address: '', lat: '10.4806', lng: '-66.9036', radius: '150', whatsapp: '', receptionPin: '', kitchenPin: '', deliveryPin: '' };
const emptyUser = { name: '', email: '', role: 'manager', password: '' };
const emptyAiConfig = { aiEnabled: true, aiMonthlyLimit: '' };

function statusTone(status: string): 'green' | 'amber' | 'red' {
  return status === 'activo' ? 'green' : status === 'suspendido' ? 'amber' : 'red';
}

function aiHealthLabel(status: BusinessListItem['aiHealthStatus']): string {
  return {
    active: 'Activo',
    disabled: 'Desactivado',
    monthly_limit_reached: 'Límite alcanzado',
    not_configured: 'No configurado',
  }[status];
}

function aiHealthTone(status: BusinessListItem['aiHealthStatus']): 'green' | 'amber' | 'red' {
  return status === 'active' ? 'green' : status === 'monthly_limit_reached' ? 'amber' : 'red';
}

function compactDiagnosticId(value: string | null): string {
  if (!value) return '—';
  return value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
}

function formatFailureTime(value: string): string {
  return new Intl.DateTimeFormat('es-VE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function tokenSummary(alert: AdminAiChatFailureItem): string {
  const parts = [
    alert.model ? `Modelo: ${alert.model}` : null,
    alert.totalTokens != null ? `Tokens: ${alert.totalTokens}` : null,
    alert.requestCount != null ? `Req: ${alert.requestCount}` : null,
    alert.messageCount != null ? `Msgs: ${alert.messageCount}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Sin métricas';
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const { show } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [branchOpen, setBranchOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [businessForm, setBusinessForm] = useState(emptyBusiness);
  const [branchForm, setBranchForm] = useState(emptyBranch);
  const [userForm, setUserForm] = useState(emptyUser);
  const [aiConfigForm, setAiConfigForm] = useState(emptyAiConfig);
  const [busy, setBusy] = useState(false);

  const { data: overview, isLoading: overviewLoading, isError: overviewError, error: overviewErrorInfo } = useAdminOverview();
  const { data: businesses = [], isLoading: businessesLoading, isError: businessesError, error: businessesErrorInfo } = useAdminBusinesses(search, filter);
  const { data: detail, isLoading: detailLoading } = useAdminBusinessDetail(selectedId);
  const { data: aiChatFailures = [], isLoading: failuresLoading, isError: failuresError, error: failuresErrorInfo } = useAdminAiChatFailures();

  useEffect(() => {
    if (overviewError) show(overviewErrorInfo instanceof Error ? overviewErrorInfo.message : 'No se pudo cargar el panel.', 'error');
    if (businessesError) show(businessesErrorInfo instanceof Error ? businessesErrorInfo.message : 'No se pudo cargar la lista de negocios.', 'error');
    if (failuresError) show(failuresErrorInfo instanceof Error ? failuresErrorInfo.message : 'No se pudieron cargar las alertas de IA.', 'error');
  }, [businessesError, businessesErrorInfo, failuresError, failuresErrorInfo, overviewError, overviewErrorInfo, show]);

  const createBusinessMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiRequest<{ businessId: string }>('/api/admin/businesses', {
      method: 'POST',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.overview }),
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.all }),
      ]);
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ businessId, status }: { businessId: string; status: BusinessListItem['status'] }) => apiRequest(`/api/admin/businesses/${businessId}/status`, {
      method: 'PATCH',
      body: jsonBody({ status }),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.overview }),
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.businesses(search, filter) }),
      ]);
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId) });
      }
    },
  });

  const createBranchMutation = useMutation({
    mutationFn: ({ businessId, payload }: { businessId: string; payload: Record<string, unknown> }) => apiRequest(`/api/admin/businesses/${businessId}/branches`, {
      method: 'POST',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId) });
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.overview });
    },
  });

  const createUserMutation = useMutation({
    mutationFn: ({ businessId, payload }: { businessId: string; payload: Record<string, unknown> }) => apiRequest(`/api/admin/businesses/${businessId}/users`, {
      method: 'POST',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId) });
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.overview });
    },
  });

  const updateAiConfigMutation = useMutation({
    mutationFn: ({ businessId, payload }: { businessId: string; payload: { aiEnabled: boolean; aiMonthlyLimit: number | null } }) => apiRequest(`/api/admin/businesses/${businessId}/ai-config`, {
      method: 'PATCH',
      body: jsonBody(payload),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.businesses(search, filter) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId ?? 'none') }),
      ]);
    },
  });

  const resolveAiChatFailureMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/admin/ai-chat-failures/${id}/resolve`, { method: 'PATCH' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.aiChatFailures('active') });
    },
  });

  useEffect(() => {
    if (!detail) return;
    setAiConfigForm({
      aiEnabled: detail.business.aiEnabled,
      aiMonthlyLimit: detail.business.aiMonthlyLimit == null ? '' : String(detail.business.aiMonthlyLimit),
    });
  }, [detail]);

  const loadDetail = (businessId: string) => {
    setSelectedId(businessId);
  };

  const createBusiness = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await createBusinessMutation.mutateAsync({
        name: businessForm.name,
        subdomain: businessForm.subdomain,
        owner: { name: businessForm.ownerName, email: businessForm.ownerEmail, password: businessForm.ownerPassword },
        branch: {
          name: businessForm.branchName,
          address: businessForm.address,
          lat: Number(businessForm.lat),
          lng: Number(businessForm.lng),
          allowedRadiusM: Number(businessForm.radius),
          whatsapp: businessForm.whatsapp || undefined,
          receptionPin: businessForm.receptionPin,
          kitchenPin: businessForm.kitchenPin,
          deliveryPin: businessForm.deliveryPin || undefined,
        },
      });
      setCreateOpen(false);
      setBusinessForm(emptyBusiness);
      show('Negocio creado correctamente.', 'success');
      loadDetail(result.businessId);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo crear el negocio.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (businessId: string, status: BusinessListItem['status']) => {
    if (!window.confirm(`¿Cambiar el negocio a estado “${status}”? Las sesiones activas pueden cerrarse.`)) return;
    try {
      await changeStatusMutation.mutateAsync({ businessId, status });
      show(`Estado cambiado a ${status}.`, 'success');
      if (selectedId === businessId) loadDetail(businessId);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo cambiar el estado.', 'error');
    }
  };

  const createBranch = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;
    setBusy(true);
    try {
      await createBranchMutation.mutateAsync({
        businessId: selectedId,
        payload: {
          name: branchForm.name, address: branchForm.address, lat: Number(branchForm.lat), lng: Number(branchForm.lng),
          allowedRadiusM: Number(branchForm.radius), requireGeolocation: true, whatsapp: branchForm.whatsapp || undefined,
          active: true, receptionPin: branchForm.receptionPin, kitchenPin: branchForm.kitchenPin,
          deliveryPin: branchForm.deliveryPin || undefined,
        },
      });
      setBranchOpen(false); setBranchForm(emptyBranch); show('Sucursal creada.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo crear la sucursal.', 'error'); }
    finally { setBusy(false); }
  };

  const createUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;
    setBusy(true);
    try {
      await createUserMutation.mutateAsync({
        businessId: selectedId,
        payload: { name: userForm.name, email: userForm.email, role: userForm.role, password: userForm.password, active: true },
      });
      setUserOpen(false); setUserForm(emptyUser); show('Usuario creado.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo crear el usuario.', 'error'); }
    finally { setBusy(false); }
  };

  const saveAiConfig = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;
    setBusy(true);
    try {
      await updateAiConfigMutation.mutateAsync({
        businessId: selectedId,
        payload: {
          aiEnabled: aiConfigForm.aiEnabled,
          aiMonthlyLimit: aiConfigForm.aiMonthlyLimit.trim() ? Number(aiConfigForm.aiMonthlyLimit) : null,
        },
      });
      show('Configuración de IA actualizada.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo actualizar la configuración de IA.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const resolveAiChatFailure = async (id: string) => {
    try {
      await resolveAiChatFailureMutation.mutateAsync(id);
      show('Alerta resuelta.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo resolver la alerta.', 'error');
    }
  };

  const toggleBranch = async (branch: BusinessDetail['branches'][number]) => {
    if (!selectedId) return;
    try {
      await apiRequest(`/api/admin/businesses/${selectedId}/branches/${String(branch.id)}`, {
        method: 'PATCH',
        body: jsonBody({
          name: branch.name, address: branch.address, lat: branch.lat, lng: branch.lng,
          allowedRadiusM: branch.allowedRadiusM, requireGeolocation: branch.requireGeolocation,
          whatsapp: branch.whatsapp || undefined, active: !branch.active,
        }),
      });
      show(branch.active ? 'Sucursal desactivada.' : 'Sucursal activada.', 'success');
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId) });
      }
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo actualizar la sucursal.', 'error'); }
  };

  const toggleUser = async (member: BusinessDetail['users'][number]) => {
    if (!selectedId) return;
    try {
      await apiRequest(`/api/admin/businesses/${selectedId}/users/${String(member.id)}`, {
        method: 'PATCH',
        body: jsonBody({ name: member.name, email: member.email, role: member.role, active: !member.active }),
      });
      show(member.active ? 'Usuario desactivado.' : 'Usuario activado.', 'success');
      if (selectedId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.admin.businessDetail(selectedId) });
      }
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo actualizar el usuario.', 'error'); }
  };

  if (!user) return null;
  return (
    <div className="app-brand-shell min-h-screen">
      <header className="border-b border-[var(--app-brand-line)] bg-[var(--app-brand-ink)] text-white shadow-[0_18px_48px_rgb(26_27_24/0.18)]">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-4 py-4 sm:px-6">
          <div><div className="text-lg font-black">Estoy Pidiendo · Super Admin</div><div className="text-xs text-white/65">{user.email}</div></div>
          <Button variant="secondary" onClick={async () => { await logout(); navigate('/login'); }}>Cerrar sesión</Button>
        </div>
      </header>
      <main className="mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-8">
        <PageHeader title="Control global" description="Gestiona negocios, estados, sucursales y usuarios." action={<Button onClick={() => setCreateOpen(true)}>+ Nuevo negocio</Button>} />
        {overview && (
          <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[['Negocios', overview.businesses], ['Sucursales activas', overview.activeBranches], ['Pedidos activos', overview.activeOrders], ['Usuarios activos', overview.activeBusinessUsers]].map(([label, value]) => (
              <Card key={String(label)}><div className="app-brand-kicker">{label}</div><div className="mt-2 text-3xl font-black text-[var(--app-brand-ink)]">{value}</div></Card>
            ))}
          </div>
        )}
        <Card className="mb-6">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-black text-[var(--app-brand-ink)]">Alertas del chat IA público</h2>
              <p className="text-sm text-[var(--app-brand-muted)]">Errores activos reportados desde el asistente público. Al resolverlos dejan de aparecer en esta lista.</p>
            </div>
            <Badge tone={aiChatFailures.length ? 'red' : 'green'}>{aiChatFailures.length} activas</Badge>
          </div>
          {failuresLoading ? <LoadingScreen label="Cargando alertas…" /> : aiChatFailures.length ? (
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label="Alertas activas del chat IA público" className="min-w-[1080px]">
                  <Table.Header>
                    <Table.Column>Fecha</Table.Column>
                    <Table.Column>Negocio</Table.Column>
                    <Table.Column>Sucursal</Table.Column>
                    <Table.Column>Mensaje</Table.Column>
                    <Table.Column>Error</Table.Column>
                    <Table.Column>Contexto</Table.Column>
                    <Table.Column>Diagnóstico</Table.Column>
                    <Table.Column className="text-end">Acciones</Table.Column>
                  </Table.Header>
                  <Table.Body>
                    {aiChatFailures.map((alert) => (
                      <Table.Row key={alert.id}>
                        <Table.Cell><div className="text-xs font-bold text-[var(--app-brand-ink)]">{formatFailureTime(alert.createdAt)}</div></Table.Cell>
                        <Table.Cell><div className="font-bold text-[var(--app-brand-ink)]">{alert.businessName}</div><div className="text-xs text-[var(--app-brand-muted)]">{alert.businessSubdomain}</div></Table.Cell>
                        <Table.Cell>{alert.branchName}</Table.Cell>
                        <Table.Cell><p className="max-w-[260px] truncate" title={alert.userMessage}>{alert.userMessage}</p></Table.Cell>
                        <Table.Cell><div className="font-bold text-red-700">{alert.errorCode}</div><div className="max-w-[260px] text-xs text-[var(--app-brand-muted)]" title={alert.errorMessage}>{alert.errorStatus} · {alert.errorMessage}</div></Table.Cell>
                        <Table.Cell><div className="text-xs">{tokenSummary(alert)}</div></Table.Cell>
                        <Table.Cell><div className="text-xs text-[var(--app-brand-muted)]">Req: <span title={alert.requestId ?? undefined}>{compactDiagnosticId(alert.requestId)}</span></div><div className="text-xs text-[var(--app-brand-muted)]">Sesión: <span title={alert.sessionHash}>{compactDiagnosticId(alert.sessionHash)}</span></div>{alert.errorDetail && <div className="mt-1 max-w-[240px] truncate text-xs text-[var(--app-brand-muted)]" title={alert.errorDetail}>{alert.errorDetail}</div>}</Table.Cell>
                        <Table.Cell><div className="flex justify-end"><Button variant="ghost" busy={resolveAiChatFailureMutation.isPending} onClick={() => void resolveAiChatFailure(alert.id)}>Resolver</Button></div></Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          ) : <EmptyState title="Sin fallas activas de IA" description="No hay alertas pendientes del chat público." />}
        </Card>
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(420px,.85fr)]">
          <Card>
            <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_180px]">
              <Input placeholder="Buscar por nombre o subdominio" value={search} onChange={(event) => setSearch(event.target.value)} />
              <HeroSelectField value={filter || null} onChange={(value) => setFilter(String(value ?? ''))} options={[{ id: '', label: 'Todos los estados' }, { id: 'activo', label: 'Activo' }, { id: 'suspendido', label: 'Suspendido' }, { id: 'bloqueado', label: 'Bloqueado' }]} />
            </div>
            {overviewLoading || businessesLoading ? <LoadingScreen label="Cargando negocios…" /> : businesses.length ? (
              <Table>
                <Table.ScrollContainer>
                  <Table.Content aria-label="Negocios" className="min-w-[860px]">
                    <Table.Header>
                      <Table.Column isRowHeader>Negocio</Table.Column>
                      <Table.Column>Estado</Table.Column>
                      <Table.Column>IA</Table.Column>
                      <Table.Column>Sucursales</Table.Column>
                      <Table.Column>Pedidos</Table.Column>
                      <Table.Column className="text-end">Acciones</Table.Column>
                    </Table.Header>
                    <Table.Body>
                      {businesses.map((item) => (
                        <Table.Row key={item.id} className={selectedId === item.id ? 'bg-[var(--app-brand-paper)]' : ''}>
                          <Table.Cell>
                            <button type="button" onClick={() => void loadDetail(item.id)} className="text-left">
                              <div className="font-bold text-[var(--app-brand-ink)]">{item.name}</div>
                              <div className="text-xs text-[var(--app-brand-muted)]">{item.subdomain}</div>
                            </button>
                          </Table.Cell>
                          <Table.Cell><Badge tone={statusTone(item.status)}>{item.status}</Badge></Table.Cell>
                          <Table.Cell>
                            <div className="text-xs">
                              <Badge tone={aiHealthTone(item.aiHealthStatus)}>{aiHealthLabel(item.aiHealthStatus)}</Badge>
                              <div className="text-[var(--app-brand-muted)]">{item.currentMonthAiUsage.requestCount}/{item.aiMonthlyLimit ?? '∞'} req</div>
                            </div>
                          </Table.Cell>
                          <Table.Cell>{item.branchCount}</Table.Cell>
                          <Table.Cell>{item.orderCount} <span className="text-xs text-amber-600">({item.activeOrderCount} activos)</span></Table.Cell>
                          <Table.Cell>
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" onClick={() => void loadDetail(item.id)}>Ver</Button>
                              {item.status !== 'activo' && <Button variant="ghost" onClick={() => void changeStatus(item.id, 'activo')}>Activar</Button>}
                              {item.status === 'activo' && <Button variant="ghost" onClick={() => void changeStatus(item.id, 'suspendido')}>Suspender</Button>}
                              <Button variant="ghost" onClick={() => void changeStatus(item.id, 'bloqueado')}>Bloquear</Button>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Content>
                </Table.ScrollContainer>
              </Table>
            ) : <EmptyState title="Sin negocios" description="Crea el primer negocio para comenzar." />}
          </Card>

          <Card className="min-h-[500px]">
            {!selectedId ? <EmptyState title="Selecciona un negocio" description="Verás aquí sus sucursales, usuarios y datos principales." /> : detailLoading ? <LoadingScreen /> : detail ? (
              <div className="grid gap-6">
                <div><div className="flex items-start justify-between gap-3"><div><h2 className="text-xl font-black text-[var(--app-brand-ink)]">{detail.business.name}</h2><p className="text-sm text-[var(--app-brand-muted)]">{detail.business.subdomain}</p></div><Badge tone={statusTone(detail.business.status)}>{detail.business.status}</Badge></div></div>
                <AdminPublicMenuLayoutPanel key={selectedId} businessId={selectedId} />
                <section className="grid gap-4 rounded-2xl border border-[var(--app-brand-line)] bg-[var(--app-brand-paper)]/70 p-4"><div className="flex items-center justify-between gap-3"><div><h3 className="font-black text-[var(--app-brand-ink)]">Asistente IA</h3><p className="text-xs text-[var(--app-brand-muted)]">Estado operativo global para este negocio.</p></div><Badge tone={aiHealthTone(detail.business.aiHealthStatus)}>{aiHealthLabel(detail.business.aiHealthStatus)}</Badge></div><form onSubmit={saveAiConfig} className="grid gap-4 sm:grid-cols-2"><HeroSelectField label="Estado" value={aiConfigForm.aiEnabled ? 'enabled' : 'disabled'} onChange={(value) => setAiConfigForm((current) => ({ ...current, aiEnabled: String(value ?? 'disabled') === 'enabled' }))} options={[{ id: 'enabled', label: 'Habilitado' }, { id: 'disabled', label: 'Deshabilitado' }]} /><Field label="Límite mensual de requests"><Input type="number" min="1" placeholder="Sin límite" value={aiConfigForm.aiMonthlyLimit} onChange={(event) => setAiConfigForm((current) => ({ ...current, aiMonthlyLimit: event.target.value.replace(/[^0-9]/g, '') }))} /></Field><div className="flex justify-end sm:col-span-2"><Button type="submit" busy={busy}>Guardar IA</Button></div></form><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{[['Requests', detail.currentMonthAiUsage.requestCount], ['Mensajes', detail.currentMonthAiUsage.messageCount], ['Input tokens', detail.currentMonthAiUsage.inputTokens ?? '—'], ['Total tokens', detail.currentMonthAiUsage.totalTokens ?? '—']].map(([label, value]) => <div key={String(label)} className="rounded-xl border border-[var(--app-brand-line)] bg-white/70 p-3"><div className="app-brand-kicker">{label}</div><div className="mt-1 text-lg font-black text-[var(--app-brand-ink)]">{value}</div></div>)}</div><div className="text-xs text-[var(--app-brand-muted)]">Mes: {detail.currentMonthAiUsage.billingMonth}</div></section>
                <section><div className="mb-3 flex items-center justify-between"><h3 className="font-black text-[var(--app-brand-ink)]">Sucursales</h3><Button variant="secondary" onClick={() => setBranchOpen(true)}>+ Sucursal</Button></div><div className="grid gap-2">{detail.branches.map((branch) => <div key={branch.id} className="flex items-center justify-between rounded-xl border border-[var(--app-brand-line)] bg-white/70 p-3"><div><div className="font-bold text-[var(--app-brand-ink)]">{branch.name}</div><div className="text-xs text-[var(--app-brand-muted)]">#{String(branch.correlationCurrent).padStart(2, '0')} · ciclo {String(branch.correlationCycle)}</div></div><Button variant="ghost" onClick={() => void toggleBranch(branch)}>{branch.active ? 'Desactivar' : 'Activar'}</Button></div>)}</div></section>
                <section><div className="mb-3 flex items-center justify-between"><h3 className="font-black text-[var(--app-brand-ink)]">Usuarios</h3><Button variant="secondary" onClick={() => setUserOpen(true)}>+ Usuario</Button></div><div className="grid gap-2">{detail.users.map((member) => <div key={member.id} className="flex items-center justify-between rounded-xl border border-[var(--app-brand-line)] bg-white/70 p-3"><div><div className="font-bold text-[var(--app-brand-ink)]">{member.name}</div><div className="text-xs text-[var(--app-brand-muted)]">{member.email} · {member.role}</div></div><Button variant="ghost" onClick={() => void toggleUser(member)}>{member.active ? 'Desactivar' : 'Activar'}</Button></div>)}</div></section>
              </div>
            ) : null}
          </Card>
        </div>
      </main>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Crear negocio" width="max-w-4xl">
        <form onSubmit={createBusiness} className="grid gap-6">
          <div className="grid gap-4 sm:grid-cols-2"><Field label="Nombre del negocio"><Input required value={businessForm.name} onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })} /></Field><Field label="Subdominio"><Input required pattern="[a-z0-9-]+" value={businessForm.subdomain} onChange={(e) => setBusinessForm({ ...businessForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} /></Field></div>
          <div><h3 className="mb-3 font-black">Propietario</h3><div className="grid gap-4 sm:grid-cols-3"><Field label="Nombre"><Input required value={businessForm.ownerName} onChange={(e) => setBusinessForm({ ...businessForm, ownerName: e.target.value })} /></Field><Field label="Correo"><Input type="email" required value={businessForm.ownerEmail} onChange={(e) => setBusinessForm({ ...businessForm, ownerEmail: e.target.value })} /></Field><Field label="Contraseña inicial"><Input type="password" required minLength={10} value={businessForm.ownerPassword} onChange={(e) => setBusinessForm({ ...businessForm, ownerPassword: e.target.value })} /></Field></div></div>
          <div><h3 className="mb-3 font-black">Primera sucursal</h3><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"><Field label="Nombre"><Input required value={businessForm.branchName} onChange={(e) => setBusinessForm({ ...businessForm, branchName: e.target.value })} /></Field><Field label="WhatsApp"><Input required pattern="\+?[0-9]{7,15}" value={businessForm.whatsapp} onChange={(e) => setBusinessForm({ ...businessForm, whatsapp: e.target.value })} /></Field><Field label="Latitud"><Input type="number" step="any" required value={businessForm.lat} onChange={(e) => setBusinessForm({ ...businessForm, lat: e.target.value })} /></Field><Field label="Longitud"><Input type="number" step="any" required value={businessForm.lng} onChange={(e) => setBusinessForm({ ...businessForm, lng: e.target.value })} /></Field><Field label="Dirección"><Input value={businessForm.address} onChange={(e) => setBusinessForm({ ...businessForm, address: e.target.value })} /></Field><Field label="Radio permitido (m)"><Input type="number" min="20" max="10000" required value={businessForm.radius} onChange={(e) => setBusinessForm({ ...businessForm, radius: e.target.value })} /></Field><Field label="PIN recepción"><Input required pattern="[0-9]{4}" maxLength={4} value={businessForm.receptionPin} onChange={(e) => setBusinessForm({ ...businessForm, receptionPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field><Field label="PIN cocina"><Input required pattern="[0-9]{4}" maxLength={4} value={businessForm.kitchenPin} onChange={(e) => setBusinessForm({ ...businessForm, kitchenPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field><Field label="PIN reparto (opcional)"><Input pattern="[0-9]{4}" maxLength={4} value={businessForm.deliveryPin} onChange={(e) => setBusinessForm({ ...businessForm, deliveryPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field></div></div>
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Crear negocio</Button></div>
        </form>
      </Modal>

      <Modal open={branchOpen} onClose={() => setBranchOpen(false)} title="Nueva sucursal">
        <form onSubmit={createBranch} className="grid gap-4 sm:grid-cols-2"><Field label="Nombre"><Input required value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} /></Field><Field label="WhatsApp"><Input required value={branchForm.whatsapp} onChange={(e) => setBranchForm({ ...branchForm, whatsapp: e.target.value })} /></Field><Field label="Dirección"><Input value={branchForm.address} onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })} /></Field><Field label="Radio (m)"><Input type="number" required value={branchForm.radius} onChange={(e) => setBranchForm({ ...branchForm, radius: e.target.value })} /></Field><Field label="Latitud"><Input type="number" step="any" required value={branchForm.lat} onChange={(e) => setBranchForm({ ...branchForm, lat: e.target.value })} /></Field><Field label="Longitud"><Input type="number" step="any" required value={branchForm.lng} onChange={(e) => setBranchForm({ ...branchForm, lng: e.target.value })} /></Field><Field label="PIN recepción"><Input required pattern="[0-9]{4}" value={branchForm.receptionPin} onChange={(e) => setBranchForm({ ...branchForm, receptionPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field><Field label="PIN cocina"><Input required pattern="[0-9]{4}" value={branchForm.kitchenPin} onChange={(e) => setBranchForm({ ...branchForm, kitchenPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field><Field label="PIN reparto (opcional)"><Input pattern="[0-9]{4}" value={branchForm.deliveryPin} onChange={(e) => setBranchForm({ ...branchForm, deliveryPin: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></Field><div className="flex justify-end gap-2 sm:col-span-2"><Button type="button" variant="secondary" onClick={() => setBranchOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Crear</Button></div></form>
      </Modal>

      <Modal open={userOpen} onClose={() => setUserOpen(false)} title="Nuevo usuario">
        <form onSubmit={createUser} className="grid gap-4"><Field label="Nombre"><Input required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} /></Field><Field label="Correo"><Input type="email" required value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} /></Field><HeroSelectField label="Rol" value={userForm.role} onChange={(value) => setUserForm({ ...userForm, role: String(value ?? 'manager') })} options={[{ id: 'owner', label: 'Propietario' }, { id: 'manager', label: 'Gerente' }]} /><Field label="Contraseña"><Input type="password" required minLength={10} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} /></Field><div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setUserOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Crear</Button></div></form>
      </Modal>
    </div>
  );
}
